import {
  ChevronDown,
  ChevronRight,
  Folder,
  Loader2,
  Search
} from "lucide-react";
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { MattersAPI } from "@/api/matters";
import type {
  ChecklistEntry,
  ChecklistInternalItem,
  ChecklistResponse
} from "@/api/types/foreignNationalDocument";
import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { ListCard } from "@/components/ui/list-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tag } from "@/components/ui/tag";
import { mapWithConcurrency } from "@/lib/mapWithConcurrency";
import { cn } from "@/lib/utils";

import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import type { FsNode } from "../../caseFiles/types";
import {
  expiryText,
  parseDay
} from "../../foreignNationalDocuments/components/checklist/checklistModel";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  individualId: string;
  /** The folder the copies land in; null puts them in "From client". */
  parentId: string | null;
  targetName: string;
  /** `complete` is false when some documents failed and the picker stays open. */
  onAdded: (nodes: FsNode[], result: { complete: boolean }) => void;
};

type PickRow = {
  entry: ChecklistEntry;
  title: string;
  older: ChecklistEntry[];
  /** A case folder's tag in place of the case-file one ("Internal"). */
  tag?: string | null;
  /** A case folder's row: the folder the file sits in on that case. */
  where?: string;
};

type PickGroup = { key: string; title: string; rows: PickRow[] };

/** One of the client's other cases, a folder in the Cases section. */
type CaseFolder = { individualId: string; label: string };

type CaseTree =
  | { status: "loading" }
  | { status: "error"; error: string }
  | {
      status: "ready";
      rows: PickRow[];
      /** Drafts, forms, packets and plans: not files, so they can't be copied. */
      blocked: ChecklistInternalItem[];
    };

type Failure = { entry: ChecklistEntry; error: string };

/** Saves and copies in flight at once. */
const CONCURRENCY = 4;

const inThisCase = (entry: ChecklistEntry, individualId: string) =>
  entry.cases.some((c) => c.individual_id === individualId);

/** A case file already in this case has nothing to add. */
const pickable = (entry: ChecklistEntry, individualId: string) =>
  entry.source === "client" ||
  !entry.cases.every((c) => c.individual_id === individualId);

const tagFor = (
  entry: ChecklistEntry,
  alreadyAdded: boolean,
  folderTag: string | null | undefined
): string | null => {
  if (alreadyAdded) return "In This Case";
  if (folderTag !== undefined) return folderTag;
  if (entry.source === "case")
    return `Case File · ${entry.cases[0]?.case_label ?? "Another Case"}`;
  return null;
};

/** The sections of the client's Documents tab the picker keeps apart. */
const OWN_SECTIONS = new Set(["identity", "status"]);

/** Where a file sits in its case: "/Identity" for "/Identity/Passport". */
const folderOf = (node: FsNode): string => {
  const path = node.path_cache || "";
  const cut = path.lastIndexOf("/");
  return cut > 0 ? path.slice(0, cut) : "Top Level";
};

/**
 * A file on another case as a pickable row. A file the checklist already
 * lists keeps its entry (and id), so picking it here or above is one pick.
 */
const treeRow = (
  node: FsNode,
  folder: CaseFolder,
  known: Map<string, ChecklistEntry>
): PickRow => {
  const key = `${folder.individualId}:${node.id}`;
  const title = node.title || node.name;
  const filename = node.extension ? `${title}.${node.extension}` : title;
  const tag =
    node.document_role === "internal"
      ? "Internal"
      : node.document_role === "work_product" || node.source_ref
      ? "Work Product"
      : null;
  const entry: ChecklistEntry = known.get(key) ?? {
    id: key,
    document_id: null,
    source: "case",
    slot: null,
    display_name: null,
    download_url: null,
    added_at: node.created_at ?? null,
    added_by: { kind: "case", name: null, case_label: folder.label },
    facts: {},
    expires: null,
    expiry_tone: null,
    state: "current",
    review: null,
    filing_status: null,
    client_upload: false,
    shared_with_client: false,
    cases: [
      {
        case_id: "",
        individual_id: folder.individualId,
        case_label: folder.label,
        node_id: node.id
      }
    ],
    node: { individual_id: folder.individualId, node_id: node.id },
    signature: null,
    title,
    filename,
    mime_type: node.mime_type ?? null,
    size_bytes: node.size_bytes ?? null,
    summary: folderOf(node)
  };
  return { entry, title, older: [], tag, where: folderOf(node) };
};

const SectionHeader: FC<{ title: string; divided: boolean }> = ({
  title,
  divided
}) => (
  <div
    className={cn(
      "flex h-7 items-center px-4 text-sm font-medium text-fg-3 bg-fill-1",
      divided && "border-t border-line-1"
    )}
  >
    {title}
  </div>
);

const documents = (n: number) => `${n} ${n === 1 ? "document" : "documents"}`;

const messageOf = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Something went wrong.";

const describe = (
  entry: ChecklistEntry,
  today: Date,
  withFilename: boolean
): string =>
  [
    entry.summary,
    entry.expires ? expiryText(entry, today) : null,
    withFilename ? entry.filename : null
  ]
    .filter(Boolean)
    .join(" · ");

const groupsOf = (
  data: ChecklistResponse,
  individualId: string
): PickGroup[] => {
  const rowsOf = (section: ChecklistResponse["sections"][number]) =>
    section.slots.flatMap((slot) =>
      slot.entries
        .filter((e) => pickable(e, individualId))
        .map((entry, i) => ({
          entry,
          title: entry.display_name || slot.name,
          older:
            i === 0 ? slot.older.filter((e) => pickable(e, individualId)) : []
        }))
    );
  const groups: PickGroup[] = data.sections
    .filter((section) => OWN_SECTIONS.has(section.kind))
    .map((section) => ({
      key: section.kind,
      title: section.title,
      rows: rowsOf(section)
    }));
  groups.push({
    key: "other",
    title: "Other",
    rows: [
      ...data.sections
        .filter((section) => !OWN_SECTIONS.has(section.kind))
        .flatMap(rowsOf),
      ...data.other.map((entry) => ({
        entry,
        title: entry.filename || entry.title || "Document",
        older: []
      }))
    ]
  });
  return groups.filter((g) => g.rows.length > 0);
};

/** Every entry the checklist knows, by each case file it is (`individual:node`). */
const entriesByCaseFile = (
  data: ChecklistResponse
): Map<string, ChecklistEntry> => {
  const out = new Map<string, ChecklistEntry>();
  const all = [
    ...data.sections.flatMap((s) =>
      s.slots.flatMap((slot) => [...slot.entries, ...slot.older])
    ),
    ...data.other
  ];
  for (const e of all) {
    if (e.node) out.set(`${e.node.individual_id}:${e.node.node_id}`, e);
    for (const use of e.cases)
      if (use.node_id) out.set(`${use.individual_id}:${use.node_id}`, e);
  }
  return out;
};

/** The client's cases other than this one, labelled as the checklist labels them. */
const otherCases = (
  data: ChecklistResponse,
  matters: Array<{ individual_id: string; visa?: string | null }>,
  individualId: string
): CaseFolder[] => {
  const labels = new Map<string, string>();
  for (const g of data.internal ?? [])
    labels.set(g.individual_id, g.case_label);
  const all = [
    ...data.sections.flatMap((s) => s.slots.flatMap((slot) => slot.entries)),
    ...data.other
  ];
  for (const e of all)
    for (const use of e.cases)
      if (use.individual_id && !labels.has(use.individual_id))
        labels.set(use.individual_id, use.case_label);
  const seen = new Set<string>([individualId]);
  const out: CaseFolder[] = [];
  for (const m of matters) {
    if (m.individual_id && !seen.has(m.individual_id)) {
      seen.add(m.individual_id);
      out.push({
        individualId: m.individual_id,
        label: labels.get(m.individual_id) ?? (m.visa || "Case")
      });
    }
  }
  return out;
};

const matches = (row: PickRow, q: string) =>
  [
    row.title,
    row.entry.filename,
    row.entry.summary,
    ...row.older.map((o) => o.filename)
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(q);

/**
 * "From Client Documents": pick documents the client already provided, laid
 * out like the client's Documents tab (Identity, Status, a folder per other
 * case, Other), and copy them into the case folder the upload dialog targets.
 * The client keeps their copies. A file from another case is copied straight
 * from that case; its drafts, forms and packets aren't files, so they show
 * disabled. This case has no folder: its files are already here, and
 * documents already in it show checked and can't be picked again.
 */
export const ClientDocumentsPicker: FC<Props> = ({
  open,
  onOpenChange,
  individualId,
  parentId,
  targetName,
  onAdded
}) => {
  const [data, setData] = useState<ChecklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [foreignNationalId, setForeignNationalId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  /** Copied into the case while the picker is open. */
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
    via: "all" | "picked";
  } | null>(null);
  const running = useRef(false);
  const anchor = useRef<string | null>(null);
  const [matters, setMatters] = useState<
    Array<{ individual_id: string; visa?: string | null }>
  >([]);
  const [openCases, setOpenCases] = useState<Set<string>>(new Set());
  const [trees, setTrees] = useState<Record<string, CaseTree>>({});
  const adding = progress !== null;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setData(null);
    setLoading(true);
    setError(null);
    setPicked(new Set());
    setExpanded(new Set());
    setAdded(new Set());
    setQuery("");
    setMatters([]);
    setOpenCases(new Set());
    setTrees({});
    anchor.current = null;
    FilesystemAPI.listClientDocuments(individualId)
      .then(async (res) => {
        const fnId = res.foreign_national_id;
        if (cancelled) return;
        setForeignNationalId(fnId);
        if (!fnId) return;
        // The case folders come in when the case list does; without it the
        // picker still offers the rest.
        MattersAPI.listForForeignNational(fnId)
          .then((cases) => {
            if (!cancelled)
              setMatters(
                cases.map((m) => ({
                  individual_id: m.individual_id,
                  visa: m.case?.visa ?? null
                }))
              );
          })
          .catch(() => undefined);
        const checklist = await ForeignNationalDocumentsAPI.getChecklist(fnId);
        if (!cancelled) setData(checklist);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Couldn't load documents.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, individualId]);

  const today = useMemo(
    () => (data?.today ? parseDay(data.today) : new Date()),
    [data?.today]
  );
  const groups = useMemo(
    () => (data ? groupsOf(data, individualId) : []),
    [data, individualId]
  );
  const caseFolders = useMemo(
    () => (data ? otherCases(data, matters, individualId) : []),
    [data, matters, individualId]
  );
  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      q
        ? groups
            .map((g) => ({ ...g, rows: g.rows.filter((r) => matches(r, q)) }))
            .filter((g) => g.rows.length > 0)
        : groups,
    [groups, q]
  );
  /** Rows each open, loaded case folder shows. */
  const folderRows = useMemo(() => {
    const out = new Map<string, PickRow[]>();
    for (const folder of caseFolders) {
      const tree = trees[folder.individualId];
      if (openCases.has(folder.individualId) && tree?.status === "ready")
        out.set(
          folder.individualId,
          q ? tree.rows.filter((r) => matches(r, q)) : tree.rows
        );
    }
    return out;
  }, [caseFolders, openCases, q, trees]);
  const byId = useMemo(() => {
    const out = new Map<string, { entry: ChecklistEntry; title: string }>();
    for (const g of groups)
      for (const r of g.rows) {
        out.set(r.entry.id, { entry: r.entry, title: r.title });
        for (const o of r.older)
          out.set(o.id, { entry: o, title: o.filename ?? r.title });
      }
    for (const tree of Object.values(trees))
      if (tree.status === "ready")
        for (const r of tree.rows)
          if (!out.has(r.entry.id))
            out.set(r.entry.id, { entry: r.entry, title: r.title });
    return out;
  }, [groups, trees]);

  const toggleCase = (folder: CaseFolder) => {
    const id = folder.individualId;
    setOpenCases((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (trees[id] && trees[id].status !== "error") return;
    setTrees((t) => ({ ...t, [id]: { status: "loading" } }));
    const known = data ? entriesByCaseFile(data) : new Map();
    FilesystemAPI.getTree(id)
      .then((res) => {
        const rows = res.nodes
          .filter((n) => n.kind === "file" && !n.is_deleted)
          .map((n) => treeRow(n, folder, known));
        const blocked = (data?.internal ?? [])
          .filter((g) => g.individual_id === id)
          .flatMap((g) => g.items)
          .filter((item) => item.kind !== "file");
        setTrees((t) => ({ ...t, [id]: { status: "ready", rows, blocked } }));
      })
      .catch((e: unknown) =>
        setTrees((t) => ({
          ...t,
          [id]: { status: "error", error: messageOf(e) }
        }))
      );
  };

  const isAdded = (entry: ChecklistEntry) =>
    added.has(entry.id) || inThisCase(entry, individualId);

  /** Rows in screen order: Identity, Status, then the case folders, then Other. */
  const screenRows: PickRow[] = [
    ...visible.filter((g) => g.key !== "other").flatMap((g) => g.rows),
    ...caseFolders.flatMap((f) => folderRows.get(f.individualId) ?? []),
    ...visible.filter((g) => g.key === "other").flatMap((g) => g.rows)
  ];
  const unique = (entries: ChecklistEntry[]) => {
    const seen = new Set<string>();
    return entries.filter((e) => !seen.has(e.id) && Boolean(seen.add(e.id)));
  };
  /** Add All and Select All cover the current version of each row shown. */
  const shown = unique(screenRows.map((r) => r.entry)).filter(
    (e) => !isAdded(e)
  );
  /** Rows in screen order, for shift-click ranges. */
  const order = unique(
    screenRows.flatMap((r) => [
      r.entry,
      ...(expanded.has(r.entry.id) ? r.older : [])
    ])
  )
    .filter((e) => !isAdded(e))
    .map((e) => e.id);
  const shownPicked = shown.filter((e) => picked.has(e.id)).length;
  const allState =
    shown.length > 0 && shownPicked === shown.length
      ? true
      : shownPicked > 0
      ? ("indeterminate" as const)
      : false;

  const pick = (id: string, extend: boolean) => {
    const from = extend && anchor.current ? order.indexOf(anchor.current) : -1;
    const to = order.indexOf(id);
    const on = !picked.has(id);
    const range =
      from >= 0 && to >= 0
        ? order.slice(Math.min(from, to), Math.max(from, to) + 1)
        : [id];
    anchor.current = id;
    setPicked((s) => {
      const next = new Set(s);
      for (const r of range) {
        if (on) next.add(r);
        else next.delete(r);
      }
      return next;
    });
  };

  const toggleAll = () =>
    setPicked((s) => {
      const next = new Set(s);
      for (const e of shown) {
        if (allState === true) next.delete(e.id);
        else next.add(e.id);
      }
      return next;
    });

  const destination = parentId ? targetName : "From client";

  const titleOf = (entry: ChecklistEntry) =>
    byId.get(entry.id)?.title ?? entry.filename ?? "Document";

  const run = async (entries: ChecklistEntry[], via: "all" | "picked") => {
    const fnId = foreignNationalId;
    if (!fnId || entries.length === 0 || running.current) return;
    running.current = true;
    const total = entries.length;
    let done = 0;
    const settle = (n: number) => {
      done += n;
      setProgress({ done, total, via });
    };
    setProgress({ done, total, via });

    const failures: Failure[] = [];
    const landed: string[] = [];
    const nodes: FsNode[] = [];

    /** Copy a batch in one call, or one at a time when the batch fails. */
    const importBatch = async (
      batch: ChecklistEntry[],
      copy: (items: ChecklistEntry[]) => Promise<{ nodes: FsNode[] }>
    ) => {
      const one = async (entry: ChecklistEntry) => {
        try {
          nodes.push(...(await copy([entry])).nodes);
          landed.push(entry.id);
        } catch (e) {
          failures.push({ entry, error: messageOf(e) });
        } finally {
          settle(1);
        }
      };
      if (batch.length === 1) {
        await one(batch[0]);
        return;
      }
      try {
        nodes.push(...(await copy(batch)).nodes);
        landed.push(...batch.map((e) => e.id));
        settle(batch.length);
      } catch {
        // One bad document fails the whole batch, so copy them one at a time
        // to land the rest. At the root the first copy creates "From client".
        const rest = parentId ? batch : batch.slice(1);
        if (!parentId) await one(batch[0]);
        await mapWithConcurrency(rest, CONCURRENCY, one);
      }
    };

    // The client's documents, then each other case's files straight from that
    // case, one batch after another so only the first makes "From client".
    const vault = entries.filter((e) => e.document_id);
    const bySource = new Map<string, ChecklistEntry[]>();
    for (const entry of entries.filter((e) => !e.document_id)) {
      if (entry.node) {
        const list = bySource.get(entry.node.individual_id) ?? [];
        list.push(entry);
        bySource.set(entry.node.individual_id, list);
      } else {
        failures.push({ entry, error: "This document can't be copied." });
        settle(1);
      }
    }
    const batches: Array<() => Promise<void>> = [
      ...(vault.length
        ? [
            () =>
              importBatch(vault, (items) =>
                FilesystemAPI.importFromClient(
                  individualId,
                  items.map((e) => e.document_id as string),
                  parentId
                )
              )
          ]
        : []),
      ...[...bySource].map(
        ([source, batch]) =>
          () =>
            importBatch(batch, (items) =>
              FilesystemAPI.importFromCase(
                individualId,
                source,
                items.map((e) => e.node?.node_id as string),
                parentId
              )
            )
      )
    ];
    await batches.reduce<Promise<void>>(
      (prev, next) => prev.then(next),
      Promise.resolve()
    );

    running.current = false;
    setProgress(null);
    const complete = failures.length === 0;
    if (landed.length) setAdded((s) => new Set([...s, ...landed]));
    setPicked(new Set(failures.map((f) => f.entry.id)));
    if (nodes.length) onAdded(nodes, { complete });

    if (complete) {
      toast.success(`Added ${documents(landed.length)} to ${destination}`);
      onOpenChange(false);
      return;
    }
    const failed = failures.map((f) => f.entry);
    toast.error(
      landed.length
        ? `Added ${landed.length} of ${documents(total)} to ${destination}`
        : `Couldn't add ${
            failures.length === 1 ? "the document" : documents(failures.length)
          }`,
      {
        description: (
          <span className="flex flex-col" data-testid="client-docs-failures">
            {failures.map((f) => (
              <span key={f.entry.id}>
                {titleOf(f.entry)}: {f.error}
              </span>
            ))}
          </span>
        ),
        duration: 20_000,
        action: {
          label: "Retry",
          onClick: () => void run(failed, via)
        }
      }
    );
  };

  const addPicked = () =>
    run(
      [...picked]
        .map((id) => byId.get(id)?.entry)
        .filter((e): e is ChecklistEntry => e !== undefined && !isAdded(e)),
      "picked"
    );

  const row = (
    entry: ChecklistEntry,
    title: ReactNode,
    indent: boolean,
    withFilename: boolean,
    /** In a case folder: its tag, where the file sits, and its own test id. */
    inFolder?: { tag: string | null; where: string }
  ) => {
    const inCase = isAdded(entry);
    const checked = inCase || picked.has(entry.id);
    const tag = tagFor(entry, inCase, inFolder ? inFolder.tag : undefined);
    return (
      <div
        key={`${inFolder ? "case:" : ""}${entry.id}`}
        role="checkbox"
        aria-checked={checked}
        aria-disabled={inCase || adding || undefined}
        tabIndex={inCase ? -1 : 0}
        onMouseDown={(e) => {
          if (e.shiftKey) e.preventDefault();
        }}
        onClick={(e) => {
          if (!inCase && !adding) pick(entry.id, e.shiftKey);
        }}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            if (!inCase && !adding) pick(entry.id, e.shiftKey);
          }
        }}
        className={cn(
          "flex items-center gap-3 border-t border-line-1 py-2.5 pr-4 outline-none focus-visible:bg-fill-1",
          indent ? "pl-11" : "pl-4",
          inCase
            ? "cursor-default"
            : picked.has(entry.id)
            ? "cursor-pointer bg-fill-1"
            : "cursor-pointer hover:bg-fill-1"
        )}
        data-testid={`${inFolder ? "client-case-doc" : "client-doc"}-${
          entry.id
        }`}
      >
        <Checkbox
          checked={checked}
          disabled={inCase}
          tabIndex={-1}
          aria-hidden
          className="pointer-events-none"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              "flex min-w-0 items-center gap-2 text-base",
              inCase ? "text-fg-3" : "text-fg"
            )}
          >
            <FileTypeIcon
              name={entry.filename ?? undefined}
              mimeType={entry.mime_type ?? undefined}
              size={16}
            />
            <span className="truncate">{title}</span>
          </span>
          <span className="truncate text-sm text-fg-3">
            {inFolder ? inFolder.where : describe(entry, today, withFilename)}
          </span>
        </div>
        {tag ? <Tag>{tag}</Tag> : null}
      </div>
    );
  };

  const body = () => {
    if (loading)
      return (
        <div className="flex flex-col gap-2" data-testid="client-docs-loading">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      );
    if (error)
      return <EmptyState title="Couldn't Load Documents" description={error} />;
    if (!foreignNationalId)
      return (
        <EmptyState
          title="No Client Profile"
          description="This case isn't linked to a client, so there are no client documents to add."
        />
      );
    if (groups.length === 0 && caseFolders.length === 0)
      return (
        <EmptyState
          title="No Client Documents"
          description="Documents the client uploads, or files on their other cases, show up here."
        />
      );
    return (
      <>
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-fg-3" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents…"
            aria-label="Search documents"
            className="pl-8"
          />
        </div>
        <div className="flex h-7 items-center gap-3 px-4">
          <Checkbox
            checked={allState}
            onCheckedChange={toggleAll}
            disabled={shown.length === 0 || adding}
            aria-label="Select All"
            data-testid="client-docs-select-all"
          />
          <span className="text-sm text-fg-3">
            {picked.size ? `${picked.size} Selected` : "Select All"}
          </span>
          {picked.size ? (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto"
              onClick={() => setPicked(new Set())}
              disabled={adding}
              data-testid="client-docs-clear"
            >
              Clear
            </Button>
          ) : null}
        </div>
        <ListCard className="max-h-100 overflow-y-auto">
          {visible
            .filter((g) => g.key !== "other")
            .map((group, gi) => groupBlock(group, gi > 0))}
          {caseFolders.length ? (
            <div data-testid="client-docs-group-cases">
              <SectionHeader
                title="Cases"
                divided={visible.some((g) => g.key !== "other")}
              />
              {caseFolders.map(caseFolder)}
            </div>
          ) : null}
          {visible
            .filter((g) => g.key === "other")
            .map((group) =>
              groupBlock(group, visible.length > 1 || caseFolders.length > 0)
            )}
          {visible.length === 0 && caseFolders.length === 0 ? (
            <div className="px-4 py-3 text-base text-fg-3">
              No documents match.
            </div>
          ) : null}
        </ListCard>
      </>
    );
  };

  function caseFolder(folder: CaseFolder) {
    const id = folder.individualId;
    const isOpen = openCases.has(id);
    const tree = trees[id];
    const rows = folderRows.get(id) ?? [];
    return (
      <div key={id} data-testid={`client-docs-case-${id}`}>
        <button
          type="button"
          className="flex h-9 w-full items-center gap-2 border-t border-line-1 px-4 text-left text-base text-fg hover:bg-fill-1"
          aria-expanded={isOpen}
          onClick={() => toggleCase(folder)}
          data-testid={`client-docs-case-toggle-${id}`}
        >
          {isOpen ? (
            <ChevronDown className="size-3.5 text-fg-3" />
          ) : (
            <ChevronRight className="size-3.5 text-fg-3" />
          )}
          <Folder className="size-4 text-fg-3" aria-hidden />
          <span className="truncate">{folder.label}</span>
          {tree?.status === "ready" ? (
            <span className="text-sm text-fg-3 tabular-nums">
              {tree.rows.length}
            </span>
          ) : null}
        </button>
        {!isOpen ? null : !tree || tree.status === "loading" ? (
          <div className="border-t border-line-1 py-2 pr-4 pl-11">
            <Skeleton className="h-6 w-full" />
          </div>
        ) : tree.status === "error" ? (
          <div className="border-t border-line-1 py-2 pr-4 pl-11 text-sm text-fg-3">
            Couldn&apos;t load this case&apos;s files: {tree.error}
          </div>
        ) : (
          <>
            {rows.map((r) =>
              row(r.entry, r.title, true, false, {
                tag: r.tag ?? null,
                where: r.where ?? ""
              })
            )}
            {q
              ? null
              : tree.blocked.map((item) => (
                  <div
                    key={item.id}
                    aria-disabled
                    className="flex items-center gap-3 border-t border-line-1 py-2.5 pr-4 pl-11 text-fg-3"
                    data-testid={`client-docs-blocked-${item.id}`}
                  >
                    <Checkbox
                      disabled
                      tabIndex={-1}
                      aria-hidden
                      className="pointer-events-none"
                    />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-base">{item.title}</span>
                      <span className="truncate text-sm">
                        {item.subtitle} · Add it to that case&apos;s Case Files
                        to copy it
                      </span>
                    </div>
                    <Tag>Not A File</Tag>
                  </div>
                ))}
            {rows.length === 0 && (q || tree.blocked.length === 0) ? (
              <div className="border-t border-line-1 py-2 pr-4 pl-11 text-sm text-fg-3">
                {q ? "No files match." : "No files on this case."}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  }

  function groupBlock(group: PickGroup, divided: boolean) {
    return (
      <div key={group.key} data-testid={`client-docs-group-${group.key}`}>
        <SectionHeader title={group.title} divided={divided} />
        {group.rows.map((r) => {
          const isOpen = expanded.has(r.entry.id);
          return (
            <Fragment key={r.entry.id}>
              {row(r.entry, r.title, false, group.key !== "other")}
              {r.older.length ? (
                <button
                  type="button"
                  className="flex h-6.5 w-full items-center gap-1 border-t border-line-1 pr-4 pl-11 text-sm text-fg-3 hover:text-fg"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setExpanded((s) => {
                      const next = new Set(s);
                      if (next.has(r.entry.id)) next.delete(r.entry.id);
                      else next.add(r.entry.id);
                      return next;
                    })
                  }
                  data-testid={`client-docs-older-${r.entry.id}`}
                >
                  {isOpen ? (
                    <ChevronDown className="size-3" />
                  ) : (
                    <ChevronRight className="size-3" />
                  )}
                  {r.older.length} Older{" "}
                  {r.older.length === 1 ? "Version" : "Versions"}
                </button>
              ) : null}
              {isOpen
                ? r.older.map((o) =>
                    row(
                      o,
                      <span className="text-fg-2">
                        {o.filename ?? r.title}
                      </span>,
                      true,
                      !o.filename
                    )
                  )
                : null}
            </Fragment>
          );
        })}
      </div>
    );
  }

  const progressLabel = progress ? (
    <>
      <Loader2 className="animate-spin" />
      <span data-testid="client-docs-progress">
        Adding {progress.done} of {progress.total}…
      </span>
    </>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !adding && onOpenChange(o)}>
      <DialogContent className="sm:max-w-lg" data-testid="client-docs-picker">
        <DialogHeader>
          <DialogTitle>Add From Client Documents</DialogTitle>
          <DialogDescription>
            Copies go to {destination}. The client keeps their originals.
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col gap-3">{body()}</div>
        <DialogFooter>
          <Button
            variant="secondary"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={adding}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={() => void run(shown, "all")}
            disabled={shown.length === 0 || adding}
            data-testid="client-docs-add-all"
          >
            {progress?.via === "all"
              ? progressLabel
              : `Add All (${shown.length})`}
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() => void addPicked()}
            disabled={picked.size === 0 || adding}
            data-testid="client-docs-add"
          >
            {progress?.via === "picked"
              ? progressLabel
              : picked.size
              ? `Add Selected (${picked.size})`
              : "Add Selected"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
